/**
 * @author Martin Leonov <martin.leonov@ki-wi.cz>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

/* eslint-env browser */

import EventEmitter from 'eventemitter3';

import ContentInfoParser from '../core/content/info/content-info-parser';
import ErrorName from '../core/error/error-name';
import { fetchTextThrowNotOk } from '../core/fetch/fetch';
import Queue from '../core/queue/queue';
import UnixTimestamp from '../core/unix-timestamp/unix-timestamp';

/**
 * @import { Device, EventTypes } from '.'
 * @import ContentDownloader from '../core/content/downloader/content-downloader'
 * @import { Logger } from '../core/logger/logger'
 * @import Replacer from '../core/replacer/replacer'
 * @import Store from '../core/store/store'
 * @import { UpdateUrlConfig } from '../core/update-url-config'
 * @import ViewerManager from '../core/viewer/manager/viewer-manager'
 * @import { ViewerRegistry } from '../core/viewer/registry/'
 */

/**
 * @private
 * @typedef {Record<string, *>} ContentTaskCron TODO #555
 */

/**
 * @implements {Device}
 */
export default class DevicePreview {
  /**
   * Vytvoří instanci.
   * @param {ContentDownloader} contentDownloader
   * @param {Logger} logger
   * @param {Replacer} replacer
   * @param {Store} store
   * @param {UnixTimestamp} unixTimestamp
   * @param {Pick<UpdateUrlConfig, 'contentSettings' | 'preview'>} updateUrlConfig
   * @param {ViewerManager} viewerManager
   * @param {ViewerRegistry} viewerRegistry
   */
  constructor(
    contentDownloader,
    logger,
    replacer,
    store,
    unixTimestamp,
    updateUrlConfig,
    viewerManager,
    viewerRegistry
  ) {
    this.contentDownloader = contentDownloader;
    this.logger = logger;
    this.replacer = replacer;
    this.store = store;
    this.unixTimestamp = unixTimestamp;
    this.updateUrlConfig = updateUrlConfig;
    this.viewerManager = viewerManager;
    this.viewerRegistry = viewerRegistry;

    this.applyNewContentQueue = new Queue();
    /** @type {EventEmitter<EventTypes>} */
    this.emitter = new EventEmitter();
    this.stateful = false;

    // Navázání naslouchání na instanci.
    this.onError = this.onError.bind(this);
  }

  /** @type {Device['destroy']} */
  async destroy() {
    // Zastavení obsahu.
    await this.viewerManager.setContentInfo(null);
  }

  /**
   * @param {Error} error
   * @returns {Error}
   */
  errorAsNetworkError(error) {
    const err = new Error(error.message);
    err.name = ErrorName.NetworkError;
    return err;
  }

  /** @type {Device['off']} */
  off(...args) {
    this.emitter.off(...args);
  }

  /** @type {Device['on']} */
  on(...args) {
    this.emitter.on(...args);
  }

  /**
   * Reakce na chybu.
   * @private
   * @param {Error} error Chybový objekt.
   */
  onError(error) {
    if (error.name === ErrorName.NotSupportedError) {
      return this.logger.warn(error);
    }

    return this.logger.error(error);
  }

  /** @type {Device['start']} */
  async start(_contentInfo, el) {
    this.viewerManager.setEl(el);
    const { updateUrlConfig } = this;
    if (
      updateUrlConfig.contentSettings === undefined ||
      updateUrlConfig.preview !== 'v3'
    ) {
      throw new Error('preview not configured');
    }
    const contentSettings = updateUrlConfig.contentSettings;

    // Stažení ContentSettings.
    const rawContentSettings = await fetchTextThrowNotOk(contentSettings).catch(
      (error) => {
        throw this.errorAsNetworkError(error);
      }
    );

    const contentInfo = await ContentInfoParser.parse(
      this.logger,
      this.viewerRegistry,
      rawContentSettings,
      this.store
    );

    if (contentInfo.serverUrl) {
      // Synchronizace času pro deterministické přehrávání.
      // TODO: Chtělo by to dělat periodicky jako je tomu v případě DeviceStateful.
      await fetchTextThrowNotOk(
        new URL('api/time/now', contentInfo.serverUrl).href
      )
        .then((textBody) => {
          const currentTimeMillis = parseInt(textBody, 10);
          if (isNaN(currentTimeMillis)) {
            return;
          }
          const fetchEndTimeMillis = Date.now();
          this.unixTimestamp.setOffset(
            UnixTimestamp.calculateOffset(currentTimeMillis, fetchEndTimeMillis)
          );
        })
        .catch(this.onError);
    }

    await this.replacer.applyContent(contentInfo).catch(this.onError);
    // Pokus o stažení chybějícího obsahu.
    await this.contentDownloader.download(contentInfo).catch(this.onError);
    await this.viewerManager.setContentInfo(contentInfo).catch(this.onError);

    // Vyvolání události informujícím o novém obsahu.
    this.emitter.emit('content', contentInfo);
  }
}

/**
 * @param {Pick<UpdateUrlConfig, 'preview'>} updateUrlConfig
 * @returns {void}
 * @throws {Error} Pokud nesplňuje typ.
 */
export function assertPreview(updateUrlConfig) {
  switch (updateUrlConfig.preview) {
    case 'v3':
      break;
    default:
      throw new Error(
        `${updateUrlConfig.preview} is not valid value for preview`
      );
  }
}
